import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatApiError } from "@/lib/api";
import { Loader2, LockKeyhole, Factory } from "lucide-react";
import { toast } from "sonner";

// Modül seviyesi tek seferlik okuma — StrictMode'un çift mount davranışında
// sessionStorage yalnızca bir kez tüketilir; sonraki mount'lar aynı değeri
// hafızadan alır. Kullanıcı yeni bir sekme/pencere açtığında modül yeniden
// yüklenir, bu değişken tekrar sıfırdan hesaplanır.
let __consumedLogoutReason = null;
let __consumedLogoutReasonRead = false;
function consumeLogoutReason() {
  if (__consumedLogoutReasonRead) return __consumedLogoutReason;
  __consumedLogoutReasonRead = true;
  if (typeof window === "undefined") return null;
  try {
    const r = window.sessionStorage.getItem("operix:logoutReason");
    if (r) {
      window.sessionStorage.removeItem("operix:logoutReason");
      __consumedLogoutReason = r;
      // Toast'u render dışında ertele.
      Promise.resolve().then(() => toast.error(r, { duration: 8000 }));
    }
  } catch { /* ignore */ }
  return __consumedLogoutReason;
}

// QR PDKS: oturum yokken okutulan QR, girişten sonra kaldığı yerden devam eder
const postLoginPath = () => {
  const p = sessionStorage.getItem("acuto:postLogin") || "";
  return p.startsWith("/pdks/qr?") ? p : "/";
};

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => consumeLogoutReason() || "");

  if (user && typeof user === "object") {
    const next = postLoginPath();
    sessionStorage.removeItem("acuto:postLogin");
    return <Navigate to={next} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const next = postLoginPath();
    try {
      await login(email, password);
      toast.success("Hoş geldiniz");
      sessionStorage.removeItem("acuto:postLogin");
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 relative">
        <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
        <div className="relative z-10 max-w-md w-full mx-auto lg:mx-0">
          <div className="flex items-center gap-3 mb-10">
            <img
              src="/operix-logo.png?v=8"
              alt="ACUTO 360"
              className="h-11 w-auto object-contain"
              draggable="false"
            />
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-mono">Endüstriyel ERP</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-slate-50 mb-2 tracking-tight">Kontrol Paneline Giriş</h1>
          <p className="text-slate-400 text-sm mb-8">
            Üretim akışını gerçek zamanlı yönetin.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                E-posta
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="bg-slate-900 border-slate-700 text-slate-100 rounded-sm h-11 focus-visible:ring-blue-500 focus-visible:ring-offset-slate-950"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                Şifre
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="bg-slate-900 border-slate-700 text-slate-100 rounded-sm h-11 focus-visible:ring-blue-500 focus-visible:ring-offset-slate-950"
              />
            </div>

            {error ? (
              <div
                data-testid="login-error"
                className="text-sm text-red-400 border border-red-900/50 bg-red-950/30 px-3 py-2 rounded-sm"
              >
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold uppercase tracking-wider text-xs rounded-sm transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                <LockKeyhole className="w-4 h-4 mr-2" /> Giriş Yap
              </>}
            </Button>
          </form>

          <Card className="mt-6 bg-slate-900/60 border-slate-800 p-3 rounded-sm">
            <p className="text-xs text-slate-500 font-mono">
              Kullanıcı bilgilerinizi girerek sisteme giriş yapın.
            </p>
          </Card>
        </div>
      </div>

      {/* Right: hero image */}
      <div className="hidden lg:block relative overflow-hidden border-l border-slate-800">
        <img
          src="https://images.unsplash.com/photo-1758269664127-1f744a56e06c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxmYWJyaWMlMjBtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeXxlbnwwfHx8fDE3ODQwODU4MjF8MA&ixlib=rb-4.1.0&q=85"
          alt="Fabrika"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/60 to-slate-950/90" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12">
          <div className="border-l-2 border-blue-500 pl-5 max-w-lg">
            <div className="text-xs font-mono uppercase tracking-widest text-blue-500 mb-3">
              Endüstriyel ERP · 2026
            </div>
            <h2 className="text-4xl font-bold text-slate-50 mb-3 tracking-tight leading-tight">
              Tekliften Sevkiyata 20 Aşamalı Üretim Kontrolü
            </h2>
            <p className="text-slate-400 text-sm">
              İş kıyafeti ve KKD üretim akışınızı canlı takip edin. Her koli, her metre kumaş,
              her fermuar tek bir kontrol panelinde.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
